import { Controller } from "@hotwired/stimulus";
import { placeRuler } from "../syringe_ruler.js";

const SYRINGE = "u100_units";
// The server compares exact decimals, so a hair of floating point noise is
// absorbed here rather than rejecting a draw the server accepts.
const MATCH_TOLERANCE_UNITS = 0.5 + 1e-9;
const MASS_TO_MCG = { mg: 1000, mcg: 1 };

// Mirrors Dosing::DrawHint#log_dose and Dashboard::DoseDrawHelper so the draw
// follows the dose and the vial as the member changes them, with no request.
export default class extends Controller {
  static targets = ["editor", "adjusting", "amount", "unit", "locked", "draw"];
  static values = {
    plannedAmount: Number,
    plannedUnit: String,
    enteredDraw: String,
    enteredUnit: String,
    doseUnit: String,
    editing: Boolean,
    returnsTo: String,
  };

  connect() {
    this.render();
  }

  open(event) {
    event.preventDefault();
    this.lockedTarget.hidden = true;
    this.editorTarget.hidden = false;
    this.adjustingTarget.value = "1";
    this.amountTarget.focus();
    this.amountTarget.select();
    this.render();
  }

  restore(event) {
    event.preventDefault();
    this.amountTarget.value = formatNumber(this.plannedAmountValue);
    this.unitTarget.value = this.plannedUnitValue;
    this.element.querySelectorAll('[data-amount-unit-target="choice"]').forEach((choice) => {
      choice.setAttribute("aria-pressed", String(choice.value === `unit:${this.plannedUnitValue}`));
    });
    this.lockedTarget.hidden = false;
    this.editorTarget.hidden = true;
    this.adjustingTarget.value = "";
    this.dispatch("restored");
    this.amountTarget.dispatchEvent(new Event("input", { bubbles: true }));
  }

  // The tap is read here at once while the server applies it to the cards. A tap
  // on the pick itself keeps it, as the app's rail does.
  pick(event) {
    this.picked = event.currentTarget.value.replace(/^source:/, "");
    this.render();
  }

  settle() {
    this.picked = null;
    this.render();
  }

  // A page with no source field is an edit whose sources are fixed, and the
  // server's own block already says what the draw is.
  render() {
    if (!this.hasDrawTarget || !this.sourceField) return;

    const state = this.state();
    this.renderChange(state);
    this.renderDraw(state);
  }

  state() {
    const amount = this.amount();
    const valid = amount !== null && amount > 0;
    const diverged = !valid || Math.abs(amount - this.plannedAmountValue) > 1e-9;
    const entered = this.enteredDrawValue === "" ? null : Number(this.enteredDrawValue);
    const compared =
      this.editingValue || diverged ? { amount, entered: null } : { amount: this.plannedAmountValue, entered };
    const source = this.picked ?? this.sourceField.value;
    const card = this.element.querySelector(`[data-dose-source-units-per][value="source:${source}"]`);
    const unpriced = source.startsWith("vial:") && card === null;
    // A split or a shortfall hands the draw to the plan under the cards, as the server does.
    const split = this.element.querySelector("[data-dose-source-order], [data-dose-shortfall]") !== null;
    const hint = split || unpriced ? null : this.hint(compared, valid, source, card);
    // The notes compare the protocol's own dose to the vial, whatever dose is being taken.
    const protocol =
      hint === null || this.editingValue
        ? hint
        : this.hint({ amount: this.plannedAmountValue, entered }, true, source, card);
    return { amount, valid, diverged, entered, source, card, split, unpriced, hint, protocol };
  }

  get sourceField() {
    return this.element.querySelector('input[name="dose[source]"]');
  }

  amount() {
    const text = this.amountTarget.value.trim();
    const number = Number(text);
    if (text === "" || !Number.isFinite(number)) return null;

    const from = this.unitTarget.value;
    const to = this.doseUnitValue;
    if (from === to) return number;
    if (!(from in MASS_TO_MCG) || !(to in MASS_TO_MCG)) return null;

    return (number * MASS_TO_MCG[from]) / MASS_TO_MCG[to];
  }

  hint(compared, valid, source, card) {
    if (!valid) return null;
    if (this.doseUnitValue === SYRINGE || this.doseUnitValue === "click") {
      return { kind: "draw", amount: compared.amount, unit: this.doseUnitValue, source: "dose", fit: null };
    }
    if (source.startsWith("supply:")) return null;
    if (!card) {
      const alone = this.vialCards().every((vial) => vial.dataset.doseSourceUsable !== "true");
      return alone && compared.entered !== null
        ? { kind: "draw", amount: compared.entered, unit: this.enteredUnitValue, source: "entered", fit: null }
        : null;
    }

    const calculated = units(compared.amount, card);
    const unit = card.dataset.doseSourceDevice === "pen" ? "click" : SYRINGE;
    if (compared.entered === null) return { kind: "draw", amount: calculated, unit, source: "calculated", fit: null };
    if (Math.abs(compared.entered - calculated) <= MATCH_TOLERANCE_UNITS) {
      return { kind: "draw", amount: compared.entered, unit, source: "entered", fit: null };
    }

    const fit = this.fit(compared, card);
    if (fit) return { kind: "draw", amount: calculated, unit, source: "calculated", fit };

    return { kind: "mismatch", amount: calculated, unit, source: null, fit: null };
  }

  // The vials on hand are grouped by what the dose takes from them, as the
  // server's guidance is, and every vial at the fitting strength is named.
  fit(compared, selected) {
    const fits = this.vialCards().filter((vial) => {
      if (vial === selected || vial.dataset.doseSourceUsable !== "true") return false;

      return Math.abs(compared.entered - units(compared.amount, vial)) <= MATCH_TOLERANCE_UNITS;
    });
    if (fits.length === 0) return null;

    const sameStrength = fits.filter((vial) => vial.dataset.doseSourceUnitsPer === fits[0].dataset.doseSourceUnitsPer);
    return sentence(sameStrength.map((vial) => vial.dataset.doseSourceTitle));
  }

  vialCards() {
    return Array.from(this.element.querySelectorAll("[data-dose-source-units-per]"));
  }

  renderChange({ valid, diverged }) {
    const note = this.element.querySelector("[data-dose-change]");
    if (!note) return;

    const locked = this.hasEditorTarget && this.editorTarget.hidden;
    note.hidden = locked || !valid || !diverged;
  }

  renderDraw({ amount, valid, entered, card, hint, protocol, split, unpriced, source }) {
    const draw = this.drawTarget;
    const block = draw.querySelector("[data-dose-draw]");
    block.hidden = hint === null;
    if (hint !== null) {
      block.querySelector("[data-dose-draw-verb]").textContent = hint.unit === "click" ? "Dial" : "Draw";
      block.querySelector("[data-dose-draw-title]").textContent = drawText(hint.amount, hint.unit);
      block.querySelector("[data-dose-draw-volume]").textContent = `${formatNumber(hint.amount / 100)} mL`;
      const ruler = block.querySelector("[data-dose-draw-ruler]");
      if (ruler) {
        const capacity = Number(ruler.querySelector("[data-syringe-ruler]").dataset.capacity);
        const fits = hint.unit === SYRINGE && hint.amount <= capacity;
        ruler.hidden = !fits;
        if (fits) place(ruler, hint.amount, capacity);
      }
      let note = null;
      if (protocol?.kind === "mismatch") note = "mismatch";
      else if (protocol?.fit) note = "fits";
      else if (protocol?.source === "entered" && !card) note = "alone";
      block.querySelectorAll("[data-dose-draw-note]").forEach((element) => {
        element.hidden = element.dataset.doseDrawNote !== note;
      });
      if (entered !== null) {
        block.querySelectorAll("[data-dose-entered]").forEach((element) => {
          element.textContent = drawText(entered, this.enteredUnitValue);
        });
      }
      block.querySelectorAll("[data-dose-draw-for]").forEach((element) => {
        element.textContent = doseText(amount, this.doseUnitValue);
      });
      block.querySelectorAll("[data-dose-calculated]").forEach((element) => {
        element.textContent = drawText(hint.amount, hint.unit);
      });
      if (protocol?.fit) block.querySelector("[data-dose-fit-title]").textContent = protocol.fit;
      if (card) {
        block.querySelectorAll("[data-dose-vial-title]").forEach((element) => {
          element.textContent = card.dataset.doseSourceTitle;
        });
      }
    }

    let empty = null;
    if (hint === null && !split && !unpriced && !source.startsWith("supply:")) {
      if (!valid) empty = "invalid";
    }
    draw.querySelectorAll("[data-dose-draw-empty]").forEach((element) => {
      element.hidden = element.dataset.doseDrawEmpty !== empty;
    });
    const returns = draw.querySelector("[data-dose-draw-returns]");
    if (returns) returns.hidden = !(this.editingValue && source === "none" && this.returnsToValue !== "");
    const previewed = draw.querySelector("[data-dose-preview]") !== null;
    draw.hidden = !(previewed || hint !== null || empty !== null || (returns && !returns.hidden));
  }
}

function place(wrapper, drawn, capacity) {
  const text = formatNumber(drawn);
  placeRuler(
    wrapper.querySelector("[data-syringe-ruler]"),
    drawn,
    capacity,
    `${text} ${text === "1" ? "unit" : "units"} in a ${formatNumber(capacity)} unit syringe`
  );
}

// The server rounds a drawn volume to four decimals of a millilitre, which is
// two decimals of a unit, before it prints or compares, so the browser does too.
function units(amount, card) {
  return Math.round(amount * Number(card.dataset.doseSourceUnitsPer) * 100) / 100;
}

function drawText(amount, unit) {
  const text = formatNumber(amount);
  const word = unit === "click" ? "click" : "unit";
  return `${text} ${text === "1" ? word : `${word}s`}`;
}

function doseText(amount, unit) {
  if (unit === SYRINGE || unit === "click") return drawText(amount, unit);

  return `${formatNumber(amount)} ${unit}`;
}

function sentence(titles) {
  if (titles.length <= 2) return titles.join(" or ");

  return `${titles.slice(0, -1).join(", ")}, or ${titles[titles.length - 1]}`;
}

function formatNumber(value) {
  return String(Number(value.toFixed(4)));
}
